import { serviceURL, useAuthConfig } from "@theprelude/core";
import { notifyError, notifySuccess } from "@theprelude/ds";
import ini from "ini";
import { z } from "zod";

const configSchema = z.record(
  z.string(),
  z.object({
    hq: z.string().url(),
    account: z.string(),
    handle: z.string(),
  }),
);

type Config = z.infer<typeof configSchema>;

export const useConfig = () => {
  const authConfig = useAuthConfig();

  const handleExport = async () => {
    const { account } = authConfig.credentials ?? {};
    const handle = authConfig.handle;
    if (!account || !handle) {
      notifyError({
        message: "Failed to export CLI profile: not found",
      });
      return;
    }

    const data: Config = {};

    data["default"] = {
      hq: serviceURL,
      account,
      handle,
    };

    const config = ini.encode(data, { whitespace: true });
    const blob = new Blob([config], { type: "text/plain" });
    const filename = "profile.ini";

    if ("showSaveFilePicker" in window) {
      try {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [
            {
              description: "Configuration file",
              accept: { "text/plain": [".ini"] },
            },
          ],
        });
        const fileStream = await fileHandle.createWritable();
        await fileStream.write(blob);
        await fileStream.close();

        notifySuccess({ message: "CLI profile exported successfully" });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        notifyError({ message: "Failed to open file picker", error });
      }
      return;
    }

    /** For legacy browsers */
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();

    notifySuccess({ message: "CLI profile exported successfully" });
  };

  return {
    handleExport,
    isPickerAvailable: "showOpenFilePicker" in window,
  };
};
